import type {
  CreateIntegrationBody,
  IntegrationDto,
  UpdateIntegrationBody,
} from "@evolua/api-client";

const STORAGE_KEY = "evolua.demo.integrations.v1";

type DemoStore = {
  byTenant: Record<string, IntegrationDto[]>;
};

function readStore(): DemoStore {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { byTenant: {} };
    return JSON.parse(raw) as DemoStore;
  } catch {
    return { byTenant: {} };
  }
}

function writeStore(store: DemoStore): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function assertNoCredentialInRow(row: IntegrationDto): void {
  const serialized = JSON.stringify(row);
  if (/"clientSecret"|"password"|"apiKey"/i.test(serialized)) {
    throw new Error("Credencial vazou para o store demo");
  }
}

export function listDemoIntegrations(tenantId: string): IntegrationDto[] {
  return readStore().byTenant[tenantId] ?? [];
}

export function createDemoIntegration(
  tenantId: string,
  body: CreateIntegrationBody,
): IntegrationDto {
  const store = readStore();
  const list = store.byTenant[tenantId] ?? [];
  const dup = list.find(
    (r) =>
      r.provider === body.provider &&
      (r.companyId ?? null) === (body.companyId ?? null) &&
      r.status !== "DISABLED",
  );
  if (dup) {
    throw new Error("Já existe integração para este provider e empresa");
  }

  const now = new Date().toISOString();
  const id = crypto.randomUUID();
  const row: IntegrationDto = {
    id,
    tenantId,
    companyId: body.companyId ?? null,
    provider: body.provider,
    baseUrl: body.baseUrl ?? null,
    hasSecret: true,
    secretRef: `/evolua/demo/integration/${tenantId}-${body.provider}-${id.slice(0, 8)}`,
    status: "NOT_CONFIGURED",
    version: 1,
    createdAt: now,
    updatedAt: now,
  };
  void body.credentials;
  assertNoCredentialInRow(row);
  store.byTenant[tenantId] = [...list, row];
  writeStore(store);
  return row;
}

export function updateDemoIntegration(
  tenantId: string,
  id: string,
  body: UpdateIntegrationBody,
): IntegrationDto {
  const store = readStore();
  const list = store.byTenant[tenantId] ?? [];
  const idx = list.findIndex((r) => r.id === id);
  if (idx < 0) throw new Error("Integração não encontrada");
  const current = list[idx]!;
  const next: IntegrationDto = {
    ...current,
    ...(body.baseUrl !== undefined ? { baseUrl: body.baseUrl } : {}),
    ...(body.companyId !== undefined ? { companyId: body.companyId } : {}),
    ...(body.credentials
      ? {
          hasSecret: true,
          secretRef:
            current.secretRef ??
            `/evolua/demo/integration/${tenantId}-${current.provider}-${id.slice(0, 8)}`,
        }
      : {}),
    version: current.version + 1,
    updatedAt: new Date().toISOString(),
  };
  assertNoCredentialInRow(next);
  list[idx] = next;
  store.byTenant[tenantId] = list;
  writeStore(store);
  return next;
}

export function disableDemoIntegration(tenantId: string, id: string): IntegrationDto {
  const store = readStore();
  const list = store.byTenant[tenantId] ?? [];
  const idx = list.findIndex((r) => r.id === id);
  if (idx < 0) throw new Error("Integração não encontrada");
  const next: IntegrationDto = {
    ...list[idx]!,
    status: "DISABLED",
    version: list[idx]!.version + 1,
    updatedAt: new Date().toISOString(),
  };
  list[idx] = next;
  store.byTenant[tenantId] = list;
  writeStore(store);
  return next;
}
